import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { RefreshCw, CheckCircle, XCircle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { supabase } from '@/integrations/supabase/client';
import { WEEKDAYS } from '@/lib/education-levels';

const WEEKDAY_TO_DAY_NAME: Record<number, string> = {
  1: 'monday',
  2: 'tuesday',
  3: 'wednesday',
  4: 'thursday',
  5: 'friday',
  6: 'saturday',
  0: 'sunday',
};

interface SyncResult {
  day: number;
  label: string;
  success: boolean;
  templates?: number;
  students?: number;
  deactivated?: number;
  error?: string;
}

interface SyncClassTemplatesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function SyncClassTemplatesDialog({ open, onOpenChange }: SyncClassTemplatesDialogProps) {
  const queryClient = useQueryClient();

  const allDayValues = WEEKDAYS.map((d) => d.value);
  const [selectedDays, setSelectedDays] = useState<number[]>(allDayValues);
  const [isSyncing, setIsSyncing] = useState(false);
  const [completedCount, setCompletedCount] = useState(0);
  const [currentLabel, setCurrentLabel] = useState('');
  const [results, setResults] = useState<SyncResult[]>([]);
  const [isDone, setIsDone] = useState(false);

  const toggleDay = (value: number) => {
    setSelectedDays((prev) =>
      prev.includes(value) ? prev.filter((d) => d !== value) : [...prev, value]
    );
  };

  const toggleAll = () => {
    if (selectedDays.length === WEEKDAYS.length) {
      setSelectedDays([]);
    } else {
      setSelectedDays(allDayValues);
    }
  };

  const handleSync = async () => {
    if (selectedDays.length === 0) return;

    setIsSyncing(true);
    setIsDone(false);
    setResults([]);
    setCompletedCount(0);

    // Sort selectedDays by WEEKDAYS order
    const orderedDays = WEEKDAYS.filter((d) => selectedDays.includes(d.value));

    for (let i = 0; i < orderedDays.length; i++) {
      const { value, label } = orderedDays[i];
      const dayName = WEEKDAY_TO_DAY_NAME[value];
      setCurrentLabel(`${label} (${i + 1}/${orderedDays.length})`);

      try {
        const { data, error } = await supabase.functions.invoke('sync-class-templates', {
          body: { day: dayName },
        });

        if (error) {
          setResults((prev) => [
            ...prev,
            { day: value, label, success: false, error: error.message },
          ]);
        } else {
          setResults((prev) => [
              ...prev,
              {
                day: value,
                label,
                success: true,
                templates: data?.stats?.templates_upserted ?? data?.templates_upserted,
                students: data?.stats?.students_upserted ?? data?.students_upserted,
                deactivated: data?.stats?.templates_deactivated ?? data?.templates_deactivated ?? 0,
              },
            ]);
        }
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Unknown error';
        setResults((prev) => [
          ...prev,
          { day: value, label, success: false, error: message },
        ]);
      }

      setCompletedCount(i + 1);
    }

    setIsSyncing(false);
    setIsDone(true);
    queryClient.invalidateQueries({ queryKey: ['class-templates'] });
  };

  const handleReset = () => {
    setResults([]);
    setCompletedCount(0);
    setCurrentLabel('');
    setIsDone(false);
  };

  const handleClose = () => {
    if (isSyncing) return;
    handleReset();
    onOpenChange(false);
  };

  const progressValue = selectedDays.length > 0 ? (completedCount / selectedDays.length) * 100 : 0;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Sync ข้อมูลตารางประจำ</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Day selection */}
          {!isSyncing && !isDone && (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">เลือกวันที่ต้องการ sync:</p>
              <div className="flex flex-wrap gap-2">
                {WEEKDAYS.map((day) => {
                  const isSelected = selectedDays.includes(day.value);
                  return (
                    <button
                      key={day.key}
                      onClick={() => toggleDay(day.value)}
                      className={`px-3 py-1.5 rounded-md text-sm font-medium border transition-colors ${
                        isSelected
                          ? 'bg-primary text-primary-foreground border-primary'
                          : 'bg-background text-foreground border-border hover:bg-accent'
                      }`}
                    >
                      {day.label}
                    </button>
                  );
                })}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={toggleAll}
                className="w-full"
              >
                {selectedDays.length === WEEKDAYS.length ? 'ยกเลิกทั้งหมด' : 'เลือกทุกวัน'}
              </Button>
            </div>
          )}

          {/* Progress */}
          {(isSyncing || isDone) && (
            <div className="space-y-2">
              {isSyncing && (
                <p className="text-sm text-muted-foreground">
                  กำลัง sync: <span className="font-medium text-foreground">{currentLabel}</span>
                </p>
              )}
              {isDone && (
                <p className="text-sm font-medium text-foreground">Sync เสร็จสิ้น</p>
              )}
              <Progress value={progressValue} className="h-2" />
              <p className="text-xs text-muted-foreground text-right">
                {Math.round(progressValue)}%
              </p>
            </div>
          )}

          {/* Results */}
          {results.length > 0 && (
            <div className="space-y-1.5 max-h-52 overflow-y-auto">
              {results.map((result) => (
                <div
                  key={result.day}
                  className={`flex items-start gap-2 text-sm p-2 rounded-md ${
                    result.success ? 'bg-muted/50' : 'bg-destructive/10'
                  }`}
                >
                  {result.success ? (
                    <CheckCircle className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                  ) : (
                    <XCircle className="h-4 w-4 text-destructive mt-0.5 shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <span className="font-medium">{result.label}: </span>
                    {result.success ? (
                      <span className="text-muted-foreground">
                        {result.templates ?? '?'} templates,{' '}
                        {result.students ?? '?'} students
                        {(result.deactivated ?? 0) > 0 && (
                          <span className="text-destructive">, {result.deactivated} deactivated</span>
                        )}
                      </span>
                    ) : (
                      <span className="text-destructive truncate">{result.error}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          {isDone ? (
            <>
              <Button variant="outline" onClick={handleReset}>
                Sync อีกครั้ง
              </Button>
              <Button onClick={handleClose}>ปิด</Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={handleClose} disabled={isSyncing}>
                ยกเลิก
              </Button>
              <Button
                onClick={handleSync}
                disabled={isSyncing || selectedDays.length === 0}
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${isSyncing ? 'animate-spin' : ''}`} />
                {isSyncing ? 'กำลัง Sync...' : 'เริ่ม Sync'}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
